import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { LocaleProvider, detectLocale } from "@/lib/i18n/locale";
import type { QueryClient } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ConsultationModalProvider } from "@/hooks/use-consultation-modal";
import ScrollToTop from "@/components/ScrollToTop";
import SitewideSchema from "@/components/SitewideSchema";
import StickyMobileCta from "@/components/StickyMobileCta";
import {
  installBookingConfirmationTracking,
  installPhoneClickTracking,
} from "@/lib/conversions";
import { installAnalytics } from "@/lib/analytics-init";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import appCss from "../styles.css?url";

// Toaster overlays are lazy-loaded so the sonner + Radix toast JS (~20 KiB)
// never blocks first paint or LCP. They render nothing visible until a toast
// fires, so the Suspense fallback is null — zero layout shift.
const SonnerToaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const RadixToaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);

const fontHref =
  "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap";

/** Default OG/social image (permanent CDN URL) inherited by every route. */
const DEFAULT_OG_IMAGE =
  "https://assets.cdn.filesafe.space/k94aHcRKI8Yx46SYfgfo/media/6a888227b647fb6eca258f39.png";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    // Trailing-slash → clean URL (301) is handled at the edge by
    // public/_redirects (`/(/.*)/  /$1  301`) and public/.htaccess,
    // which return the 301 before SSR runs. Throwing a Response here
    // breaks SSR serialization (Seroval cannot serialize Response).
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          content:
            "width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=5.0",
        },
        { name: "apple-mobile-web-app-capable", content: "yes" },
        { name: "apple-mobile-web-app-status-bar-style", content: "default" },
        { name: "theme-color", content: "#102A43" },
        // Base social metadata inherited by every route. Each route's own
        // head() overrides title/description/og:title/og:url as needed.
        {
          property: "og:site_name",
          content: "Neuro Wellness TMS Centers of America",
        },
        { property: "og:type", content: "website" },
        { property: "og:image", content: DEFAULT_OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: DEFAULT_OG_IMAGE },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        // Keep preconnects to a maximum of 3 (the 3 origins actually used
        // on first paint). Extra preconnects waste a connection slot.
        {
          rel: "preconnect",
          href: "https://vibe.filesafe.space",
          crossOrigin: "anonymous",
        },
        {
          rel: "preconnect",
          href: "https://assets.cdn.filesafe.space",
          crossOrigin: "anonymous",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        // Preload the hero image (above-the-fold LCP element) so it starts
        // before the stylesheet parses. AVIF is served first (smallest),
        // WebP as the fallback for non-AVIF browsers.
        {
          rel: "preload",
          as: "image",
          href: "https://vibe.filesafe.space/1792357746068722743/assets/bc70fc58-7752-4f87-a621-5d23b9edf2da.avif",
          imagesrcset:
            "https://vibe.filesafe.space/1792357746068722743/assets/bc70fc58-7752-4f87-a621-5d23b9edf2da.avif 640w, https://vibe.filesafe.space/1792357746068722743/assets/e6c9832c-3893-43f3-9410-295b34817fa0.avif 960w, https://vibe.filesafe.space/1792357746068722743/assets/7b2b3379-32b7-4937-aea9-d0c4285f6820.avif 1280w, https://vibe.filesafe.space/1792357746068722743/assets/ece1b5f7-f53d-4224-8caa-1b2212f2f0ec.avif 1600w, https://vibe.filesafe.space/1792357746068722743/assets/7e72c4a7-8949-4247-afb6-427e323bc6f1.avif 1920w",
          imagesizes: "100vw",
          fetchPriority: "high",
        },
        { rel: "preload", as: "style", href: fontHref },
        { rel: "stylesheet", href: fontHref },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(pathname || "/");
  return (
    <html lang={locale === "es" ? "es" : "en"} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <LocaleProvider locale={locale}>{children}</LocaleProvider>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => installAnalytics(), []);
  useEffect(() => installPhoneClickTracking(), []);
  useEffect(() => installBookingConfirmationTracking(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <SonnerToaster />
          <RadixToaster />
        </Suspense>
        <ConsultationModalProvider>
          <SitewideSchema />
          <ScrollToTop />
          <StickyMobileCta />
          <Outlet />
        </ConsultationModalProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function NotFoundComponent() {
  return (
    <>
      <meta name="robots" content="noindex, follow" />
      <main className="flex min-h-screen flex-col items-center justify-center bg-muted px-4 py-16">
        <div className="mx-auto max-w-lg text-center">
          <p className="font-headings text-6xl font-extrabold text-primary sm:text-7xl">
            404
          </p>
          <h1 className="mt-4 text-xl font-bold text-foreground sm:text-2xl">
            Oops! Page not found
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            The page you&apos;re looking for may have been moved or no longer
            exists.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center rounded-xl bg-primary px-6 py-3 font-heading font-bold text-primary-foreground shadow-glow transition-opacity hover:opacity-90"
          >
            Return to Home
          </Link>
        </div>
      </main>
    </>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-16">
      <div className="max-w-lg text-center">
        <h1 className="font-headings text-3xl font-bold text-foreground">
          This page didn&apos;t load
        </h1>
        <p className="mt-3 text-muted-foreground">
          Please try again, or return to the home page.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <button
            className="rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-xl border border-border bg-card px-5 py-3 font-semibold text-foreground"
          >
            Go home
          </Link>
        </div>
      </div>
    </main>
  );
}
